import { memo, useCallback, useEffect, useState, useRef } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CircleHelpIcon } from 'lucide-react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CharacterCounter } from '@/components/atoms/CharacterCounter/CharacterCounter';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import ReorderWrapper from '@/components/atoms/ReorderWrapper/ReorderWrapper';
import ToolkitAccordionWrapper from '@/components/organisms/Toolkit/ToolkitAccordionWrapper';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useDialogs from '@/hooks/useDialogs';
import useWhatsAppActive from '@/hooks/useWhatsAppActive';
import {
  isAlertThisCardButton,
  selectHasCarouselDialogErrors,
} from '@/redux/dialogAlerts/selectors';
import {
  DEFAULT_HEIGHT,
  DEFAULT_RANDOM_URL,
  getRandomElement,
  randId,
} from '@/redux/dialogs/helper';
import { updateCardButton } from '@/redux/nodes/actions';
import { selectBrainId } from '@/redux/session/selectors';
import { CarouselButton as CarouselButtonType } from '@/shared/types/action';
import { formatErrorMessage, scrollToElementById } from '@/util/util';
import { WHATSAPP_BUTTON_TEXT_LIMIT } from '@/util/validation/constants';
import { carouselButtonSchema } from '@/util/validation/schemas/carousel-schemas';
import { getWhatsappButtonLabelLength } from '@/util/validation/schemas/whatsapp-schemas';

import { defaultActionOptions } from './constants';
import CarouselDefaultActionNew from './SelectDefaultAction';

interface CarouselButtonProps {
  index: number;
  option: CarouselButtonType;
  actionId: string;
  cardIndex: number | null;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  dropItem: () => void;
  getIndex: () => number;
  handleDeleteButton: (index: number) => void;
  disabled: boolean;
  updateErrors?: (
    name: string,
    value: string | undefined,
    opts?: { cardIndex?: number; buttonIndex?: number }
  ) => void;
}

const CarouselButton = ({
  option,
  index,
  actionId,
  cardIndex,
  handleDeleteButton,
  moveItem,
  dropItem,
  getIndex,
  disabled,
  updateErrors,
}: CarouselButtonProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const defaultValues = { ...option };

  const brainId = useSelector(selectBrainId);
  const { eventsOptions } = useDialogs(brainId);
  const randomTriggerNodeId = getRandomElement(eventsOptions)?.value;
  const blockReordering = useSelector(selectHasCarouselDialogErrors(actionId));
  const isAlertThisIndex = useSelector(isAlertThisCardButton(cardIndex, index));

  const formMethods = useForm<CarouselButtonType>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(
      carouselButtonSchema(option?.hasDuplicatedLabels)
    ) as Resolver<CarouselButtonType>,
  });
  const {
    formState: { errors },
    getValues,
    unregister,
    control,
    trigger,
    setValue,
  } = formMethods;

  const watchLabel = useWatch({ control, name: 'label' }) ?? '';
  const whatsAppActive = useWhatsAppActive();

  const [openOption, setOpenOption] = useState(isAlertThisIndex);
  if (isAlertThisIndex) {
    scrollToElementById(`label-${index}-${cardIndex}`);
  }

  const toggleButtonAccordion = useCallback(() => {
    setOpenOption((prev) => !prev);
  }, []);

  const updateRedux = useCallback(() => {
    dispatch(
      updateCardButton({ actionId, cardIndex, buttonIndex: index, action: { ...getValues() } })
    );
  }, [actionId, cardIndex, dispatch, getValues, index]);

  const handleDelete = useCallback(() => {
    // Clear errors
    const opts = { cardIndex, buttonIndex: index };
    updateErrors('label', undefined, opts);
    updateErrors('buttonUrl', undefined, opts);
    updateErrors('value', undefined, opts);

    // Update redux
    handleDeleteButton(index);
  }, [cardIndex, handleDeleteButton, index, updateErrors]);

  const handleButtonTypeChange = useCallback(
    (type: string) => {
      const opts = { cardIndex, buttonIndex: index };

      if (type === 'postback') {
        setValue('value', t('actions.generate_text', { 0: randId() }), { shouldValidate: true });

        unregister(['url', 'height', 'trigger_node_id']);

        updateErrors('buttonUrl', undefined, opts);
      } else if (type === 'phone') {
        setValue('value', '123456789', { shouldValidate: true });

        unregister(['url', 'height', 'trigger_node_id']);

        updateErrors('buttonUrl', undefined, opts);
      } else if (type === 'webview') {
        setValue('height', DEFAULT_HEIGHT);
        setValue('url', DEFAULT_RANDOM_URL, { shouldValidate: true });
        setValue('trigger_node_id', randomTriggerNodeId);

        unregister('value');

        updateErrors('value', undefined, opts);
      } else if (type === 'url') {
        setValue('url', DEFAULT_RANDOM_URL, { shouldValidate: true });

        unregister(['value', 'height', 'trigger_node_id']);

        updateErrors('value', undefined, opts);
      }

      updateRedux();
    },
    [cardIndex, index, randomTriggerNodeId, setValue, t, unregister, updateErrors, updateRedux]
  );

  // Dialog Errors
  const labelErrorMessage = formatErrorMessage(errors.label);
  const urlErrorMessage = formatErrorMessage(errors?.['url']);
  const valueErrorMessage = formatErrorMessage(errors.value);

  useEffect(() => {
    trigger();
  }, [trigger, option.hasDuplicatedLabels]);

  // Track previous error messages to prevent unnecessary dispatches
  const prevErrorsRef = useRef<{ label?: string; url?: string; value?: string }>({});

  useEffect(() => {
    if (cardIndex === null || !updateErrors) {
      return;
    }

    const prevErrors = prevErrorsRef.current;
    const opts = { cardIndex, buttonIndex: index };

    // Only dispatch if label error has changed
    if (labelErrorMessage !== prevErrors.label) {
      updateErrors('label', labelErrorMessage || undefined, opts);
      prevErrors.label = labelErrorMessage;
    }
  }, [index, labelErrorMessage, updateErrors, cardIndex]);

  useEffect(() => {
    if (cardIndex === null || !updateErrors) {
      return;
    }

    const prevErrors = prevErrorsRef.current;
    const opts = { cardIndex, buttonIndex: index };

    // Only dispatch if URL error has changed
    if (urlErrorMessage !== prevErrors.url) {
      updateErrors('buttonUrl', urlErrorMessage || undefined, opts);
      prevErrors.url = urlErrorMessage;
    }
  }, [urlErrorMessage, updateErrors, index, cardIndex]);

  useEffect(() => {
    if (cardIndex === null || !updateErrors) {
      return;
    }

    const prevErrors = prevErrorsRef.current;
    const opts = { cardIndex, buttonIndex: index };

    // Only dispatch if value error has changed
    if (valueErrorMessage !== prevErrors.value) {
      updateErrors('value', valueErrorMessage || undefined, opts);
      prevErrors.value = valueErrorMessage;
    }
  }, [valueErrorMessage, updateErrors, index, cardIndex]);

  return (
    <ReorderWrapper
      dragId={`carousel-button-${cardIndex}`}
      index={index}
      moveItem={moveItem}
      dropItem={dropItem}
      getIndex={getIndex}
      placeholderHeight={36}
      canDrag={!openOption && !blockReordering}
    >
      <ToolkitAccordionWrapper
        variant="white"
        title={option.label}
        onChange={toggleButtonAccordion}
        expanded={openOption}
        handleDelete={handleDelete}
        isNested
        disabled={disabled}
      >
        <div className="pt-3">
          <Form {...formMethods}>
            <FormField
              control={control}
              name="label"
              render={({ field }) => (
                <FormItem id={`label-${index}-${cardIndex}`} className="mb-6">
                  <FormLabel className="flex items-center gap-1">
                    {t('common.label')}
                    {whatsAppActive && (
                      <InfoTooltip content={t('dialog.webview.label_tooltip')} side="right">
                        <CircleHelpIcon size={12} className="text-foreground-muted" />
                      </InfoTooltip>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('dialog.carousel.button')}
                      onChange={(event) => {
                        field.onChange(event);
                        updateRedux();
                      }}
                    />
                  </FormControl>
                  <FormMessage>{labelErrorMessage}</FormMessage>
                </FormItem>
              )}
            />
            {whatsAppActive && (
              <div className="mb-3 flex justify-end">
                <CharacterCounter
                  count={getWhatsappButtonLabelLength(watchLabel)}
                  max={32}
                  warningAt={WHATSAPP_BUTTON_TEXT_LIMIT}
                />
              </div>
            )}

            <div className="flex flex-col gap-3">
              <FormField
                control={control}
                name="type"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);
                      handleButtonTypeChange(value);
                    }}
                  >
                    <SelectTrigger id={`${actionId}-button-action-${index}-${cardIndex}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {defaultActionOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {t(option.label_key)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <CarouselDefaultActionNew
                updateRedux={updateRedux}
                actionId={actionId}
                index={index}
                cardIndex={cardIndex}
                control={control}
              />
            </div>
          </Form>
        </div>
      </ToolkitAccordionWrapper>
    </ReorderWrapper>
  );
};

export default memo(CarouselButton);
